/*
 * pep-free-gift.js
 * ---------------------------------------------------------------------------
 * Auto-adds ONE $0 gift product to the cart whenever there is at least one
 * paid item, and removes it when there are none (or when the feature is off).
 *
 * This theme has NO custom cart events (no cart:update / pub-sub). Its cart
 * engine (assets/global.js) mutates the cart with fetch() to /cart/add|change
 * and then imperatively updates ".cart-count" and re-renders the drawer
 * section. So we hook by wrapping window.fetch to detect those mutations,
 * reconcile the gift, and refresh the count + drawer ourselves.
 *
 * Availability: we skip adding when the gift is known out of stock
 * (G.available === false) AND we stop retrying after any real add failure
 * (e.g. 422 "sold out"), so a stale availability flag can never cause a spam
 * loop of failed requests.
 *
 * Config comes from window.PEP_GIFT (see snippets/pep-hidden-guard.liquid).
 */
(function () {
  var G = window.PEP_GIFT || {};
  if (!G.handle || !G.variantId) return;

  var QTY = 1;             // 1 free gift per ORDER. For 1 per paid item: QTY = paidCount.
  var busy = false;        // reconcile re-entrancy guard
  var mine = false;        // marks our own cart writes so the fetch hook ignores them
  var giftBlocked = false; // set true after a real add rejection (sold out) — stop retrying

  var R = window.routes || {};
  var CART_URL   = R.cart_url || '/cart';
  var ADD_URL    = R.cart_add_url || '/cart/add';
  var CHANGE_URL = R.cart_change_url || '/cart/change';

  function getCart() {
    return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // POST JSON and resolve to { ok, status, data } — never rejects on 4xx/5xx.
  function postJSON(url, body) {
    mine = true;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(
        function (res) { mine = false; return res; },
        function (e) { mine = false; throw e; }
      );
  }

  function updateCount(count) {
    var els = document.querySelectorAll('.cart-count');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = count;
      els[i].hidden = (count === 0);
    }
  }

  function refreshDrawer() {
    return fetch(CART_URL + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('.cart-drawer');
        var live = document.querySelector('.cart-drawer');
        if (fresh && live) { live.innerHTML = fresh.innerHTML; }
      })
      .catch(function () {});
  }

  function reconcile() {
    if (busy) return Promise.resolve();
    busy = true;
    var added = false;
    return getCart()
      .then(function (cart) {
        var gift = null, paid = 0;
        (cart.items || []).forEach(function (i) {
          if (i.handle === G.handle) { gift = i; } else { paid += i.quantity; }
        });
        var enabled = G.enabled !== false;
        var want = (enabled && paid > 0) ? QTY : 0;
        var have = gift ? gift.quantity : 0;

        if (have === want) return false;                                    // already correct
        if (want === 0) {                                                   // remove gift
          return postJSON(CHANGE_URL + '.js', { id: gift.key, quantity: 0 }).then(function () { return true; });
        }
        if (!gift) {                                                        // add gift
          if (giftBlocked || G.available === false) return false;          // known sold out — don't spam
          return postJSON(ADD_URL + '.js', { id: G.variantId, quantity: want }).then(function (res) {
            if (!res.ok) { giftBlocked = true; return false; }             // sold out / rejected — stop trying
            added = true;
            return true;
          });
        }
        return postJSON(CHANGE_URL + '.js', { id: gift.key, quantity: want }).then(function () { return true; }); // fix qty
      })
      .then(function (changed) {
        if (!changed) return null;
        return getCart().then(function (c) {
          updateCount(c.item_count);
          // On the dedicated cart page the line items are server-rendered, so
          // mirror global.js and reload to reflect the change.
          if (document.body.classList.contains('template-cart')) {
            window.location.reload();
            return null;
          }
          return refreshDrawer().then(function () {
            if (added && G.showPopup !== false) showToast(G.message);
          });
        });
      })
      .catch(function () {})
      .then(function () { busy = false; }, function () { busy = false; });
  }

  // Hook: observe cart mutations made by the theme (global.js) or the injected
  // checkout script, then reconcile. Our own writes set `mine` so we don't loop.
  var _fetch = window.fetch;
  window.fetch = function (input) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var p = _fetch.apply(this, arguments);
    if (!mine && /\/cart\/(add|change|update|clear)/.test(url)) {
      p.then(function () { window.setTimeout(reconcile, 80); }).catch(function () {});
    }
    return p;
  };

  // ---- "gift added" toast ----
  var toastEl, hideTimer;
  function buildToast() {
    var t = document.createElement('div');
    t.className = 'pep-gift-toast';
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    t.innerHTML =
      '<span class="pep-gift-toast__icon" aria-hidden="true">🎁</span>' +
      '<span class="pep-gift-toast__msg"></span>' +
      '<button type="button" class="pep-gift-toast__close" aria-label="Dismiss">×</button>';
    t.querySelector('.pep-gift-toast__close').addEventListener('click', hideToast);
    document.body.appendChild(t);
    return t;
  }
  function showToast(msg) {
    if (!toastEl) toastEl = buildToast();
    toastEl.querySelector('.pep-gift-toast__msg').textContent = msg || 'Free gift added to your order!';
    void toastEl.offsetWidth; // reflow so the transition runs on a fresh node
    toastEl.classList.add('is-visible');
    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(hideToast, 4500);
  }
  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-visible');
    window.clearTimeout(hideTimer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
